import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useTranslation } from '../../i18n';

const API = API_BASE_URL.replace(/\/+$/, '');

const INDICATORS = [
  { key: 'fatiga', es: 'Fatiga', en: 'Fatigue',
    labels: { es: ['Muy fatigado','Más fatigado','Normal','Recuperado','Muy recuperado'],
               en: ['Very fatigued','More fatigued','Normal','Recovered','Very recovered'] } },
  { key: 'calidadSueno', es: 'Sueño', en: 'Sleep',
    labels: { es: ['Insomnio','Anormal','Normal','Relajante','Muy relajante'],
               en: ['Insomnia','Abnormal','Normal','Relaxing','Very relaxing'] } },
  { key: 'danoMuscular', es: 'Muscular', en: 'Muscular',
    labels: { es: ['Muy dolorido','Más dolor','Normal','Buenas sens.','Muy buenas'],
               en: ['Very sore','More sore','Normal','Good feeling','Very good'] } },
  { key: 'nivelEstres', es: 'Estrés', en: 'Stress',
    labels: { es: ['Muy estresado','Estresado','Normal','Relajado','Muy relajado'],
               en: ['Very stressed','Stressed','Normal','Relaxed','Very relaxed'] } },
  { key: 'humor', es: 'Humor', en: 'Mood',
    labels: { es: ['Muy molesto','Mal genio','Normal','Buen humor','Humor positivo'],
               en: ['Very upset','Bad mood','Normal','Good mood','Positive mood'] } },
];

const STATE_LABELS = { es: ['Muy bajo','Bajo','Normal','Bueno','Muy bueno'], en: ['Very low','Low','Normal','Good','Very good'] };

const DEFAULTS = { fatiga: 3, calidadSueno: 3, danoMuscular: 3, nivelEstres: 3, humor: 3 };

const WellnessForm = ({ athleteId, activityId, date }) => {
  const { language } = useTranslation();
  const lang = language === 'en' ? 'en' : 'es';
  const [valuesPre, setValuesPre] = useState({ ...DEFAULTS });
  const [valuesPost, setValuesPost] = useState({ ...DEFAULTS });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!athleteId || !date) return;
    const d = typeof date === 'string' ? date.split('T')[0] : new Date(date).toISOString().split('T')[0];
    axios.get(`${API}/api/wellness/${athleteId}?date=${d}`)
      .then(r => {
        const records = Array.isArray(r.data) ? r.data : [];
        const recPre = records.find(x => x.timing === 'pre');
        const recPost = records.find(x => x.timing === 'post');
        if (recPre) setValuesPre({ fatiga: recPre.fatiga, calidadSueno: recPre.calidadSueno, danoMuscular: recPre.danoMuscular, nivelEstres: recPre.nivelEstres, humor: recPre.humor });
        if (recPost) setValuesPost({ fatiga: recPost.fatiga, calidadSueno: recPost.calidadSueno, danoMuscular: recPost.danoMuscular, nivelEstres: recPost.nivelEstres, humor: recPost.humor });
        if (recPre || recPost) setSaved(true);
      }).catch(() => {});
  }, [athleteId, date]);

  const handleSave = async () => {
    if (!athleteId) return;
    try {
      setLoading(true); setError('');
      const d = typeof date === 'string' ? date : new Date(date).toISOString().split('T')[0];
      await Promise.all([
        axios.post(`${API}/api/wellness`, { athleteId, activityId, date: d, timing: 'pre', ...valuesPre }),
        axios.post(`${API}/api/wellness`, { athleteId, activityId, date: d, timing: 'post', ...valuesPost }),
      ]);
      setSaved(true);
    } catch (e) {
      setError(lang === 'es' ? 'Error guardando wellness' : 'Error saving wellness');
    } finally { setLoading(false); }
  };

  const updateValue = (timing, key, val) => {
    setSaved(false);
    if (timing === 'pre') setValuesPre(v => ({ ...v, [key]: val }));
    else setValuesPost(v => ({ ...v, [key]: val }));
  };

  return (
    <div style={{ marginTop: 16, padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
      <div style={{ fontWeight: 700, color: '#193255', fontSize: 14, marginBottom: 10 }}>Wellness</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 38, fontSize: 10, fontWeight: 700, color: '#94a3b8', marginBottom: 10, paddingRight: 6 }}>
        <span>{lang === 'es' ? 'Pre' : 'Pre'}</span>
        <span>{lang === 'es' ? 'Post' : 'Post'}</span>
      </div>

      {INDICATORS.map(ind => (
        <div key={ind.key} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 62, fontSize: 11, fontWeight: 700, color: '#193255' }}>{ind[lang]}</div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#193255', textAlign: 'right', marginBottom: 3 }}>
                  {STATE_LABELS[lang][valuesPre[ind.key] - 1]}
                </div>
                <input type="range" min="1" max="5" step="1" value={valuesPre[ind.key]}
                  onChange={e => updateValue('pre', ind.key, Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#193255' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textAlign: 'right', marginBottom: 3 }}>
                  {STATE_LABELS[lang][valuesPost[ind.key] - 1]}
                </div>
                <input type="range" min="1" max="5" step="1" value={valuesPost[ind.key]}
                  onChange={e => updateValue('post', ind.key, Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#92400e' }} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 2, paddingLeft: 72 }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', fontSize: 8, fontWeight: 600, color: '#64748b' }}>
              <span>{ind.labels[lang][0]}</span><span>{ind.labels[lang][4]}</span>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', fontSize: 8, fontWeight: 600, color: '#64748b' }}>
              <span>{ind.labels[lang][0]}</span><span>{ind.labels[lang][4]}</span>
            </div>
          </div>
        </div>
      ))}

      {error && <p style={{ color: '#ef4444', fontSize: 11, marginBottom: 8 }}>{error}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        {saved && <span style={{ fontSize: 11, color: '#22c55e', marginRight: 10, alignSelf: 'center' }}>
          {lang === 'es' ? 'Guardado' : 'Saved'}
        </span>}
        <button onClick={handleSave} disabled={loading}
          style={{ background: '#193255', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {loading ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}
        </button>
      </div>
    </div>
  );
};

export default WellnessForm;
